import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  useToast,
} from '@adop/ui';
import { CalendarClock, Printer, RefreshCw, Search } from 'lucide-react';
import {
  formatLocalDateTime,
  formatLocalTime,
  localParts,
  RESERVATION_STATUSES,
  RESERVATION_STATUS_TRANSITIONS,
} from '@adop/shared';
import type { ReservationStatus } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import {
  insertPrintJob,
  listPrinterDevices,
  listReservations,
  subscribeToLocation,
  updateReservation,
  type ReservationRow,
} from '../../lib/data';
import { errorMessage } from '../../lib/api';
import { groupByDay } from '../../lib/sort';
import { humanize, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { ReservationStatusBadge } from '../../components/StatusBadge';
import { ListSkeleton } from '../../components/LoadingState';

const ACTION_LABELS: Record<string, string> = {
  confirmed: 'Confirm',
  declined: 'Decline',
  seated: 'Seat',
  no_show: 'No-show',
  cancelled: 'Cancel',
};

export function ReservationsPage() {
  const { location } = useWorkspace();
  const { toast } = useToast();
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [reservations, setReservations] = useState<ReservationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);
  const devices = useAsync(() => (location ? listPrinterDevices(location.id) : Promise.resolve([])), [
    location?.id,
  ]);

  const load = useCallback(async () => {
    if (!location) return;
    try {
      setError(null);
      setReservations(await listReservations(location.id));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    setLoading(true);
    void load();
    if (!location) return;
    const channel = subscribeToLocation(location.id, ['reservations'], () => void load());
    return () => {
      void channel.unsubscribe();
    };
  }, [location, load]);

  const selected = reservations.find((reservation) => reservation.id === selectedId) ?? null;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return reservations.filter((reservation) => {
      if (status !== 'all' && reservation.status !== status) return false;
      if (needle.length > 0 && !reservation.customer_name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [reservations, status, query]);

  const groups = useMemo(
    () =>
      location
        ? groupByDay(filtered, (reservation) =>
            localParts(new Date(reservation.requested_for), location.timezone).date,
          )
        : [],
    [filtered, location],
  );

  const changeStatus = async (target: string, extra: Record<string, unknown> = {}) => {
    if (!selected) return;
    setBusy(true);
    try {
      await updateReservation(selected.id, {
        status: target as ReservationStatus,
        ...(extra as { decline_reason?: string }),
      });
      toast({ title: `Reservation ${humanize(target)}`, variant: 'success' });
      setDeclineOpen(false);
      setDeclineReason('');
      await load();
    } catch (cause) {
      toast({ title: 'Could not update reservation', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reprint = async () => {
    if (!selected || !location) return;
    const active = devices.data?.find((device) => !device.revoked_at);
    if (!active) {
      toast({ title: 'No printer paired', description: 'Pair a printer first.', variant: 'error' });
      return;
    }
    try {
      await insertPrintJob({
        restaurantId: location.restaurant_id,
        locationId: location.id,
        deviceId: active.id,
        kind: 'reservation',
        reservationId: selected.id,
      });
      toast({ title: 'Reprint queued', variant: 'success' });
    } catch (cause) {
      toast({ title: 'Could not queue reprint', description: errorMessage(cause), variant: 'error' });
    }
  };

  if (!location) {
    return <EmptyState icon={<CalendarClock className="h-6 w-6" />} title="Select a location" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Reservations"
        description="Requests and confirmed tables for this location."
        action={
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1">
            <label className="mb-1 block text-xs font-medium text-zinc-500" htmlFor="reservation-search">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                id="reservation-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Customer name"
                className="pl-9"
              />
            </div>
          </div>
          <div className="w-44">
            <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All statuses</option>
              {RESERVATION_STATUSES.map((entry) => (
                <option key={entry} value={entry}>
                  {humanize(entry)}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <ListSkeleton />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<CalendarClock className="h-6 w-6" />}
          title="No reservations"
          description="Requests will appear here in real time."
        />
      ) : (
        <div className="space-y-6">
          {groups.map(([day, rows]) => (
            <div key={day}>
              <h2 className="mb-2 text-sm font-semibold text-zinc-500">
                {new Date(`${day}T12:00:00`).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                })}
              </h2>
              <div className="space-y-2">
                {rows.map((reservation) => (
                  <button
                    key={reservation.id}
                    type="button"
                    onClick={() => setSelectedId(reservation.id)}
                    className="flex w-full flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-white p-4 text-left shadow-sm hover:border-zinc-300"
                  >
                    <span className="w-20 shrink-0 text-sm font-semibold text-zinc-900">
                      {formatLocalTime(reservation.requested_for, location.timezone)}
                    </span>
                    <span className="min-w-[8rem] flex-1 text-sm font-medium text-zinc-900">
                      {reservation.customer_name}
                    </span>
                    <span className="text-sm text-zinc-600">Party of {reservation.party_size}</span>
                    <ReservationStatusBadge status={reservation.status} />
                    <span className="text-xs text-zinc-500">{relativeTime(reservation.created_at)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={selected ? `Reservation — ${selected.customer_name}` : ''}
        size="lg"
        footer={
          selected ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => void reprint()}>
                <Printer className="h-4 w-4" /> Reprint
              </Button>
              {(RESERVATION_STATUS_TRANSITIONS[selected.status] ?? [])
                .filter((target) => (RESERVATION_STATUSES as readonly string[]).includes(target))
                .map((target) => (
                <Button
                  key={target}
                  variant={target === 'declined' || target === 'cancelled' ? 'danger' : 'primary'}
                  loading={busy}
                  onClick={() =>
                    target === 'declined' ? setDeclineOpen(true) : void changeStatus(target)
                  }
                >
                  {ACTION_LABELS[target] ?? humanize(target)}
                </Button>
              ))}
            </div>
          ) : null
        }
      >
        {selected ? (
          <div className="space-y-4 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <p className="text-zinc-600">
                <span className="font-medium text-zinc-900">Party size:</span> {selected.party_size}
              </p>
              <p className="text-zinc-600">
                <span className="font-medium text-zinc-900">When:</span>{' '}
                {formatLocalDateTime(selected.requested_for, location.timezone)}
              </p>
              <p className="text-zinc-600">{selected.customer_phone ?? 'No phone'}</p>
              <p className="text-zinc-600">{selected.customer_email ?? 'No email'}</p>
            </div>
            {selected.notes ? <p className="text-zinc-600">Notes: {selected.notes}</p> : null}
            {selected.decline_reason ? (
              <p className="text-red-600">Declined: {selected.decline_reason}</p>
            ) : null}
            <p className="text-xs text-zinc-500">Requested {relativeTime(selected.created_at)}</p>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        title="Decline reservation"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>
              Back
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => void changeStatus('declined', { decline_reason: declineReason.trim() || 'Fully booked' })}
            >
              Decline
            </Button>
          </>
        }
      >
        <Input
          label="Reason (optional)"
          value={declineReason}
          onChange={(event) => setDeclineReason(event.target.value)}
          placeholder="Fully booked"
        />
      </Modal>
    </div>
  );
}
